import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building, MapPin, TrendingUp, Users, Target, ArrowRight, Star } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useContent } from '../contexts/ContentContext';
import { getContentTranslation } from '../translations/contentTranslations';
import { landingTranslations } from '../translations/landingTranslations';
import Navbar from '../components/Navbar';
import './AllOpportunitiesPage.css';

const AllOpportunitiesPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const { investmentOpportunities } = useContent();
  const t = landingTranslations[currentLanguage] || landingTranslations.id;

  // Get active opportunities
  const activeOpportunities = investmentOpportunities.filter(opportunity => opportunity.isActive);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, []);

  const handleSectorClick = (opportunityId: number) => {
    const sectorRoutes = {
      1: 'guitar-industry',
      2: 'textile-industry', 
      3: 'agroindustry',
      4: 'furniture-industry',
      5: 'automotive-parts',
      6: 'tourism-development'
    };
    
    const route = sectorRoutes[opportunityId as keyof typeof sectorRoutes];
    if (route) {
      navigate(`/sectors/${route}`);
    }
  };

  return (
    <div className="all-opportunities-page">
      {/* Navbar */}
      <Navbar isScrolled={true} />
      
      {/* Header */}
      <div className="page-header">
        <div className="container">
          <div className="header-content">
            <div className="breadcrumb">
              <span onClick={() => navigate('/')} className="breadcrumb-link">
                {currentLanguage === 'id' ? 'Beranda' : 'Home'}
              </span>
              <ArrowRight className="w-4 h-4" />
              <span className="breadcrumb-current">
                {currentLanguage === 'id' ? 'Semua Peluang Investasi' : 'All Investment Opportunities'}
              </span>
            </div>
            <h1 className="page-title">
              {currentLanguage === 'id' ? 'Semua Peluang Investasi' : 'All Investment Opportunities'}
            </h1>
            <p className="page-description">
              {currentLanguage === 'id' 
                ? 'Jelajahi berbagai peluang investasi menarik yang telah diidentifikasi dan siap untuk dikembangkan di Kabupaten Sukoharjo.'
                : 'Explore various attractive investment opportunities that have been identified and are ready for development in Sukoharjo Regency.'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="page-content">
        <div className="container">
          {/* Statistics section removed as requested */}

          {/* Opportunities Grid */}
          <div className="opportunities-section">
            <h2 className="section-title">
              {currentLanguage === 'id' ? 'Semua Peluang Investasi' : 'All Investment Opportunities'}
            </h2>
            
            <div className="opportunities-grid">
              {activeOpportunities.map((opportunity) => (
                <div 
                  key={opportunity.id} 
                  className="opportunity-card"
                  onClick={() => handleSectorClick(opportunity.id)}
                >
                  <div className="opportunity-image">
                    <img src={opportunity.image} alt={getContentTranslation(opportunity.title, currentLanguage)} />
                    <div className="opportunity-badge">
                      <Star className="w-4 h-4" />
                      {currentLanguage === 'id' ? 'Unggulan' : 'Featured'}
                    </div>
                  </div>
                  <div className="opportunity-content">
                    <h3 className="opportunity-title">
                      {getContentTranslation(opportunity.title, currentLanguage)}
                    </h3>
                    <p className="opportunity-description">
                      {getContentTranslation(opportunity.description, currentLanguage)}
                    </p>
                    <div className="opportunity-meta">
                      <div className="meta-item">
                        <MapPin className="w-4 h-4" />
                        <span>Sukoharjo</span>
                      </div>
                      <div className="meta-item">
                        <Building className="w-4 h-4" />
                        <span>
                          {currentLanguage === 'id' ? 'Sektor Industri' : 'Industrial Sector'}
                        </span>
                      </div>
                    </div>
                    <div className="opportunity-actions">
                      <button className="btn btn-primary">
                        <ArrowRight className="w-4 h-4" />
                        {currentLanguage === 'id' ? 'Lihat Detail' : 'View Details'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="cta-section">
            <div className="cta-content">
              <h2 className="cta-title">
                {currentLanguage === 'id' ? 'Tertarik Untuk Berinvestasi?' : 'Interested in Investing?'}
              </h2>
              <p className="cta-description">
                {currentLanguage === 'id'
                  ? 'Tim ahli kami siap membantu Anda menemukan peluang investasi terbaik sesuai dengan profil dan kebutuhan bisnis Anda.'
                  : 'Our expert team is ready to help you find the best investment opportunities according to your business profile and needs.'
                }
              </p>
              <div className="cta-buttons">
                <button 
                  className="btn btn-primary"
                  onClick={() => navigate('/contact')}
                >
                  {currentLanguage === 'id' ? 'Hubungi Kami' : 'Contact Us'}
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={() => navigate('/peta-potensi')}
                >
                  <MapPin className="w-5 h-5" />
                  {currentLanguage === 'id' ? 'Lihat Peta Potensi' : 'View Potential Map'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllOpportunitiesPage;